/**
 * API Client avec Axios
 * Utilise Firebase Auth pour les tokens ID (auto-refresh)
 */

import axios, { AxiosInstance, InternalAxiosRequestConfig, AxiosError } from 'axios';
import { API_CONFIG } from '@/config/api.config';
import { auth } from '@/lib/firebase';
import type { ApiError } from './types';

// ═══════════════════════════════════════════════════════════════════════════
// Axios Instance
// ═══════════════════════════════════════════════════════════════════════════

export const apiClient: AxiosInstance = axios.create({
  baseURL: API_CONFIG.baseURL,
  timeout: API_CONFIG.timeout,
  headers: API_CONFIG.headers,
});

// ═══════════════════════════════════════════════════════════════════════════
// Request Interceptor (ajoute le token Firebase ID)
// ═══════════════════════════════════════════════════════════════════════════

apiClient.interceptors.request.use(
  async (config: InternalAxiosRequestConfig) => {
    const user = auth.currentUser;

    if (user && config.headers) {
      try {
        // Firebase gère automatiquement le refresh si le token est expiré
        // forceRefresh: false → utilise le cache si le token est valide
        const token = await user.getIdToken(false);
        config.headers.Authorization = `Bearer ${token}`;
      } catch (error) {
        console.error('Error getting Firebase ID token:', error);
      }
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// ═══════════════════════════════════════════════════════════════════════════
// Response Interceptor (gestion des erreurs 401 avec force refresh)
// ═══════════════════════════════════════════════════════════════════════════

apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError<ApiError>) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & {
      _retry?: boolean;
    };

    // Si erreur 401 et pas encore de retry
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        const user = auth.currentUser;

        if (!user) {
          // Pas d'utilisateur, rediriger vers login
          window.location.href = '/login';
          return Promise.reject(error);
        }

        // Force refresh du token Firebase
        const newToken = await user.getIdToken(true);

        // Retry la requête originale avec le nouveau token
        if (originalRequest.headers) {
          originalRequest.headers.Authorization = `Bearer ${newToken}`;
        }

        return apiClient(originalRequest);
      } catch (refreshError) {
        // Échec du refresh, déconnecter
        console.error('Error refreshing Firebase token:', refreshError);
        try {
          await auth.signOut();
        } catch {}
        window.location.href = '/login';
        return Promise.reject(refreshError);
      }
    }

    // Autres erreurs
    return Promise.reject(error);
  }
);

// ═══════════════════════════════════════════════════════════════════════════
// Helper pour gérer les erreurs API
// ═══════════════════════════════════════════════════════════════════════════

export function handleApiError(error: unknown): string {
  if (axios.isAxiosError(error)) {
    const axiosError = error as AxiosError<ApiError>;

    if (axiosError.response?.data?.message) {
      // Message d'erreur du backend
      if (Array.isArray(axiosError.response.data.message)) {
        return axiosError.response.data.message.join(', ');
      }
      return axiosError.response.data.message;
    }

    if (axiosError.message) {
      return axiosError.message;
    }
  }

  if (error instanceof Error) {
    return error.message;
  }

  return 'Une erreur inattendue est survenue';
}

export default apiClient;
